/**
 * LanguageContext.tsx
 * مصدر الحقيقة الوحيد للغة الواجهة — مستقلّ عن weatherStore عمداً
 * لأن لوحة الأدمن حزمة منفصلة تماماً بلا وصول له. يُركَّب مرّتين (App.tsx وAdminApp.tsx)
 * وكلاهما يقرأ/يكتب نفس مفتاح localStorage فيبقى الاختيار متّسقاً بين الموقعين.
 *
 * تطبيق dir/lang على <html> يحدث هنا مرّة واحدة فقط — لا حاجة لـ dir="rtl" متفرّق
 * في أي مكوّن آخر؛ كل شيء يتوارثه من الجذر تلقائياً.
 *
 * أولوية اختيار اللغة: مسار /en ⇒ ?lang= في الرابط ⇒ تخزين المستخدم ⇒ كشف الموقع (detectLang).
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AR_DICTIONARY, type DictionaryKey } from './dictionaries/ar';
import { EN_DICTIONARY } from './dictionaries/en';
import { detectLang } from './detectLang';
import { isLang, langLocale, type Lang } from './types';

export type { Lang };

const STORAGE_KEY = 'rw_lang';
type Dict = Partial<Record<string, string>>;

/**
 * العربية والإنجليزية مضمّنتان؛ البقية قطع منفصلة تُجلب عند الاختيار. القاموس الجزئي
 * مسموح: أي مفتاح غائب (لوحة الأدمن مثلاً) يسقط إلى الإنجليزية لا إلى اسم المفتاح.
 */
const BUNDLED: Partial<Record<Lang, Dict>> = { ar: AR_DICTIONARY, en: EN_DICTIONARY };
const LOADERS = import.meta.glob<{ default: Dict }>(['./dictionaries/*.ts', '!./dictionaries/ar.ts', '!./dictionaries/en.ts']);

async function loadDictionary(lang: Lang): Promise<Dict> {
    const bundled = BUNDLED[lang];
    if (bundled) return bundled;
    const loader = LOADERS[`./dictionaries/${lang}.ts`];
    if (!loader) return EN_DICTIONARY;
    const mod = await loader();
    BUNDLED[lang] = mod.default;
    return mod.default;
}

/** قيم الاستبدال داخل النص: t('settings.model.ago', { d: '3h' }) */
export type TParams = Record<string, string | number>;

function readInitialLang(): Lang {
    // مسار /en/ التزام صريح من الرابط نفسه — يفوز حتى على تفضيل محفوظ سابقاً. مطابق
    // للفحص المطابق في index.html (سكربت الإقلاع) — أي تعديل هنا يقابله تعديل هناك.
    try {
        if (window.location.pathname.replace(/\/+$/, '') === '/en') return 'en';
    } catch {
        // بيئة بلا window.location قابلة للقراءة — نتجاهل
    }
    // ?lang= قبل التفضيل المحفوظ: رابطٌ من صفحة مدينة أو صفحة هبوط بلغةٍ ما (‏/weather/de/…)
    // التزامٌ صريح من الزائر الآن، فيفوز على اختيارٍ قديم ويُحفظ مكانه.
    try {
        const fromUrl = new URLSearchParams(window.location.search).get('lang');
        if (isLang(fromUrl)) {
            try {
                localStorage.setItem(STORAGE_KEY, fromUrl);
            } catch {
                // التخزين غير متاح — اللغة تبقى للجلسة الحالية
            }
            return fromUrl;
        }
    } catch {
        // بيئة بلا window.location قابلة للقراءة — نتجاهل
    }
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (isLang(stored)) return stored;
    } catch {
        // localStorage غير متاح (وضع خاص/SSR) — نتجاهل ونكمل للكشف التلقائي
    }
    return detectLang();
}

function interpolate(template: string, params?: TParams): string {
    if (!params) return template;
    return template.replace(/\{(\w+)\}/g, (match, key) =>
        key in params ? String(params[key]) : match);
}

interface LanguageContextValue {
    lang: Lang;
    setLang: (lang: Lang) => void;
    toggleLang: () => void;
    /**
     * ترجمة مفتاح مسطّح "namespace.key". المفتاح المعروف مُتحقَّق منه وقت الترجمة؛
     * والمفاتيح المبنية ديناميكياً (مثل `layer.${id}`) تُمرَّر كنصّ ويعود المفتاح نفسه
     * إن غاب — فيظهر النقص فوراً في الواجهة بدل نصّ فارغ.
     */
    t: (key: DictionaryKey | (string & {}), params?: TParams) => string;
    dir: 'rtl' | 'ltr';
    /** وسم BCP-47 لواجهات Intl (تنسيق التواريخ والأرقام). */
    locale: string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
    const [lang, setLangState] = useState<Lang>(readInitialLang);
    // القاموس الفعّال يتأخّر عن lang لحظة التحميل الأولى للغة غير مضمّنة؛ خلالها نعرض
    // الإنجليزية بدل مفاتيح خام.
    const [dict, setDict] = useState<Dict>(() => BUNDLED[lang] ?? EN_DICTIONARY);

    useEffect(() => {
        let alive = true;
        loadDictionary(lang)
            .then((d) => { if (alive) setDict(d); })
            .catch(() => { if (alive) setDict(EN_DICTIONARY); });
        return () => { alive = false; };
    }, [lang]);

    useEffect(() => {
        document.documentElement.lang = lang;
        document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    }, [lang]);

    const setLang = (next: Lang) => {
        setLangState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch {
            // تجاهل فشل التخزين — التبديل يبقى يعمل لجلسة الصفحة الحالية
        }
    };

    const toggleLang = () => setLang(lang === 'ar' ? 'en' : 'ar');

    const value = useMemo<LanguageContextValue>(() => {
        return {
            lang,
            setLang,
            toggleLang,
            t: (key: string, params?: TParams) =>
                interpolate(dict[key] ?? (EN_DICTIONARY as Dict)[key] ?? key, params),
            dir: lang === 'ar' ? 'rtl' : 'ltr',
            locale: langLocale(lang),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang, dict]);

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useTranslation(): LanguageContextValue {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error('useTranslation must be used within LanguageProvider');
    return ctx;
}
